import { useNavigate } from "react-router-dom";

import {
  Bot,
  Building2,
  Clock,
  ExternalLink,
  Mail,
  MessageSquare,
  Phone,
  ShoppingCart,
} from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "~/components/ui/sheet";
import { useLeadCotizaciones } from "~/hooks/useClientApprovals";
import { useOptimizedProfiles } from "~/hooks/useOptimizedProfiles";
import {
  ClientApprovalPayload,
  ClientApprovalRequest,
} from "~/types/clientApproval";
import { formatDate, formatRut } from "~/utils/helpers";

import { ApprovalStatusBadge } from "./ApprovalStatusBadge";

interface ApprovalDetailSheetProps {
  request: ClientApprovalRequest | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApproveClick: () => void;
  onRejectClick: () => void;
}

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex justify-between gap-4 border-b border-gray-200 py-1.5 text-sm last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className="max-w-[60%] text-right font-medium text-gray-800">
        {value || "-"}
      </span>
    </div>
  );
}

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon?: typeof Building2;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
      <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-100 px-4 py-2.5">
        {Icon && <Icon className="h-4 w-4 text-gray-600" />}
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export function ApprovalDetailSheet({
  request,
  open,
  onOpenChange,
  onApproveClick,
  onRejectClick,
}: ApprovalDetailSheetProps) {
  const navigate = useNavigate();

  const reviewerIds = [request?.reviewed_by, request?.assigned_seller].filter(
    Boolean
  ) as string[];
  const { getUserName } = useOptimizedProfiles(reviewerIds);

  const { cotizaciones, isLoading: cotizacionesLoading } = useLeadCotizaciones(
    open && request
      ? {
          email: request.email,
          phone: request.phone_number,
          company: request.name,
        }
      : null
  );

  if (!request) return null;

  const payload = (request.payload ?? {}) as unknown as ClientApprovalPayload;
  const addr = payload.fiscal_address;
  const addressLine = addr
    ? [
        [addr.street, addr.number].filter(Boolean).join(" "),
        addr.complement,
        addr.city_label,
        addr.state_label,
      ]
        .filter(Boolean)
        .join(", ")
    : null;

  const isPending = request.status === "pending";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader className="mb-4">
          <div className="flex items-center gap-2">
            <SheetTitle className="text-left">{request.name}</SheetTitle>
            <ApprovalStatusBadge status={request.status} />
          </div>
          <SheetDescription className="text-left">
            {formatRut(request.tax_id)}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4">
          <Section title="Datos del cliente" icon={Building2}>
            <InfoRow label="Razón social" value={request.name} />
            <InfoRow label="RUT" value={formatRut(request.tax_id)} />
            <InfoRow label="Contacto" value={request.contact_person} />
            <InfoRow label="Email" value={request.email} />
            <InfoRow label="Teléfono" value={request.phone_number} />
            <InfoRow label="Giro" value={request.activity} />
            <InfoRow label="Zona" value={payload.zone} />
            <InfoRow label="Notas internas" value={payload.internal_notes} />
          </Section>

          <Section title="Dirección fiscal">
            <InfoRow label="Dirección" value={addressLine} />
          </Section>

          {(payload.collection_contact_person ||
            payload.collection_contact_email ||
            payload.collection_contact_phone) && (
            <Section title="Contacto de cobranza">
              <InfoRow
                label="Nombre"
                value={payload.collection_contact_person}
              />
              <InfoRow label="Email" value={payload.collection_contact_email} />
              <InfoRow
                label="Teléfono"
                value={payload.collection_contact_phone}
              />
            </Section>
          )}

          <Section title="Origen" icon={Bot}>
            <InfoRow
              label="Fuente"
              value={
                request.source === "nexor_ai"
                  ? "Agente IA (Nexor)"
                  : request.source
              }
            />
            <InfoRow label="Agente" value={request.created_by_agent} />
            <InfoRow label="Lead ID" value={request.nexor_lead_id} />
            <InfoRow
              label="Workflow run"
              value={request.nexor_workflow_run_id}
            />
            <InfoRow label="Cliente Nexor" value={request.nexor_client_id} />
            <InfoRow label="Recibida" value={formatDate(request.created_at)} />
            {request.is_test && <InfoRow label="Prueba" value="Sí" />}
          </Section>

          {request.status === "approved" && (
            <Section title="Resolución">
              <InfoRow
                label="Vendedor asignado"
                value={
                  request.assigned_seller
                    ? getUserName(request.assigned_seller)
                    : "Sin asignar"
                }
              />
              <InfoRow
                label="Aprobado por"
                value={getUserName(request.reviewed_by)}
              />
              <InfoRow label="Fecha" value={formatDate(request.reviewed_at)} />
              {request.created_client_id && (
                <div className="pt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      navigate(`/clients/${request.created_client_id}`)
                    }
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Ver cliente
                  </Button>
                </div>
              )}
            </Section>
          )}

          {request.status === "rejected" && (
            <Section title="Resolución">
              <InfoRow
                label="Rechazado por"
                value={getUserName(request.reviewed_by)}
              />
              <InfoRow label="Fecha" value={formatDate(request.reviewed_at)} />
              <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {request.rejection_reason || "Sin motivo especificado."}
              </div>
            </Section>
          )}

          <Section title="Cotizaciones del lead" icon={MessageSquare}>
            {cotizacionesLoading ? (
              <p className="text-sm text-muted-foreground">Buscando…</p>
            ) : cotizaciones.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No se encontraron cotizaciones asociadas.
              </p>
            ) : (
              <div className="space-y-2">
                {cotizaciones.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => navigate(`/cotizaciones/${c.id}`)}
                    className="flex w-full flex-col gap-1 rounded-md border border-gray-200 bg-gray-50 p-3 text-left transition-colors hover:bg-gray-100"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-gray-800">
                        {c.full_name}
                      </span>
                      <span className="whitespace-nowrap text-xs text-gray-500">
                        {formatDate(c.created_at)}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        {c.company}
                      </span>
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {c.email}
                      </span>
                      <span className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {c.phone}
                      </span>
                      {c.is_quotation && (
                        <span className="flex items-center gap-1 text-violet-700">
                          <ShoppingCart className="h-3 w-3" />
                          Cotización
                        </span>
                      )}
                    </div>
                    <p className="line-clamp-2 text-xs text-gray-600">
                      {c.message}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </Section>

          {isPending && (
            <div className="flex gap-3 pt-2">
              <Button className="flex-1" onClick={onApproveClick}>
                Aprobar
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={onRejectClick}
              >
                Rechazar
              </Button>
            </div>
          )}

          <div className="flex items-center justify-center gap-1 pb-4 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            Solicitud creada el {formatDate(request.created_at)}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
